import { create } from 'zustand';
import { supabase } from '@/lib/supabase/client';
import { type BookingStatus } from '@/lib/utils/bookingFilters';

interface Booking {
  id: string;
  user_id: string;
  experience_id: string;
  status: BookingStatus;
  created_at: string;
  total_amount: number;
  participant_count?: number;
  experience?: any;
  [key: string]: any;
}

interface BookingsState {
  bookings: Booking[];
  loading: boolean;
  error: string | null;
  
  // Actions
  fetchUserBookings: (userId: string) => Promise<void>;
  addBooking: (booking: Booking) => void;
  updateBooking: (id: string, data: Partial<Booking>) => void;
  cancelBooking: (id: string) => void;
  clearError: () => void;
  reset: () => void;
}

export const useBookingsStore = create<BookingsState>((set, get) => {
  // Memoized fetch function to prevent recreation on every render
  const fetchUserBookings = async (userId: string) => {
    if (!userId) {
      console.warn('Skipping fetchUserBookings: userId is missing');
      return;
    }
    
    try {
      // Clear existing bookings before fetching to prevent showing another user's data
      set({ bookings: [], loading: true, error: null });
      console.log('Fetching bookings for user:', userId);
      
      // No need to check session here - the component already ensures user is authenticated
      // before calling this function with a userId

      const { data, error } = await supabase
        .from('bookings')
        .select(`
          *,
          experience:experiences!experience_id (
            *,
            pricing_config,
            creator:creator_id (
              id,
              full_name,
              email,
              profile_image,
              creator_profiles(
                id,
                business_name,
                contact_email,
                social_links
              )
            ),
            media:experience_media (
              url,
              type,
              order_index
            )
          )
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      console.log('Bookings fetch result:', { 
        userId,
        dataLength: data?.length,
        data: data?.map(b => ({ id: b.id, user_id: b.user_id, experience_title: b.experience?.title })),
        error 
      });

      if (error) throw error;
      
      // Process the data to unify creator_profile/creator_profiles naming
      const processedData = (data && data.length > 0) ? data.map(booking => {
        if (booking.experience?.creator?.creator_profiles) {
          // Map the creator_profiles object to creator_profile for compatibility
          return {
            ...booking,
            experience: {
              ...booking.experience,
              creator: {
                ...booking.experience.creator,
                creator_profile: booking.experience.creator.creator_profiles
              }
            }
          };
        }
        return booking;
      }) : [];
      
      // Always set the bookings array, even if empty
      set({ bookings: processedData, loading: false, error: null });
      console.log('Successfully fetched bookings for user:', userId);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
      set({ 
        bookings: [], // Clear bookings on error too
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to fetch bookings'
      });
    }
  };

  return {
    bookings: [],
    loading: false,
    error: null,

    fetchUserBookings,

    addBooking: (booking) => set((state) => ({ 
      bookings: [...state.bookings, booking] 
    })),
    
    updateBooking: (id, data) => set((state) => ({
      bookings: state.bookings.map((b) => (b.id === id ? { ...b, ...data } : b))
    })),
    
    cancelBooking: (id) => set((state) => ({
      bookings: state.bookings.map((b) => (b.id === id ? { ...b, status: 'cancelled' } : b))
    })),

    clearError: () => set({ error: null }),
    
    reset: () => set({
      bookings: [],
      loading: false,
      error: null
    })
  };
}); 